import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { useNavigate } from 'react-router-dom';
import {
  cancelEmailCampaign,
  deleteEmailCampaign,
  duplicateEmailCampaign,
  listEmailCampaigns,
  mapLaravelError,
} from '../../services/emailCampaignsApi';
import EmailCampaignStatusChip from './components/EmailCampaignStatusChip';
import EmailCampaignTourDialog from './components/EmailCampaignTourDialog';

const STATUS_FILTERS = ['', 'draft', 'scheduled', 'sending', 'sent', 'cancelled', 'failed'];
const LIST_TOUR_STORAGE_KEY = 'email_campaigns_tour_list_v1';

const LIST_TOUR_STEPS = [
  {
    title: 'Filter and Search',
    description: 'Use status, date range, and search to quickly find the campaign you need.',
  },
  {
    title: 'Quick Actions',
    description: 'Open details, duplicate, or cancel scheduled campaigns directly from the table.',
  },
  {
    title: 'Create Faster',
    description: 'Click Create Campaign to open the visual builder with templates and variable support.',
  },
];

export default function EmailCampaignsListPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, per_page: 15, total: 0 });

  const [filters, setFilters] = useState({
    status: '',
    q: '',
    date_from: '',
    date_to: '',
  });

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [tourOpen, setTourOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const payload = await listEmailCampaigns({
        ...filters,
        page,
        per_page: perPage,
      });

      setRows(Array.isArray(payload?.data) ? payload.data : []);
      setMeta(payload?.meta || { current_page: page, per_page: perPage, total: 0 });
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, [filters, page, perPage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(LIST_TOUR_STORAGE_KEY);
      if (!seen) {
        setTourOpen(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const canCancel = (campaign) => String(campaign?.status || '').toLowerCase() === 'scheduled';

  const handleCancel = async (campaign) => {
    try {
      await cancelEmailCampaign(campaign.id);
      await load();
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleDuplicate = async (campaign) => {
    try {
      const payload = await duplicateEmailCampaign(campaign.id);
      const nextId = payload?.campaign?.data?.id;
      if (nextId) {
        navigate(`/admin/communications/email-campaigns/${nextId}/edit`);
        return;
      }
      await load();
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleDelete = async (campaign) => {
    const confirmed = window.confirm(`Delete "${campaign.title || 'this campaign'}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await deleteEmailCampaign(campaign.id);
      await load();
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const pageIndex = Math.max(0, Number(page || 1) - 1);

  const empty = useMemo(() => !loading && rows.length === 0, [loading, rows.length]);
  const summary = useMemo(() => rows.reduce((acc, campaign) => {
    const status = String(campaign?.status || '').toLowerCase();
    if (Object.prototype.hasOwnProperty.call(acc, status)) {
      acc[status] += 1;
    }
    return acc;
  }, {
    draft: 0,
    scheduled: 0,
    sending: 0,
    sent: 0,
    cancelled: 0,
    failed: 0,
  }), [rows]);

  const resetFilters = () => {
    setFilters({
      status: '',
      q: '',
      date_from: '',
      date_to: '',
    });
    setPage(1);
  };

  const finishTour = () => {
    // Once the tour is closed (any way), remember it so it never auto-pops again.
    try {
      window.localStorage.setItem(LIST_TOUR_STORAGE_KEY, '1');
    } catch {
      // ignore
    }
    setTourOpen(false);
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Email Campaigns
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<HelpOutlineOutlinedIcon />}
              onClick={() => setTourOpen(true)}
            >
              Tour
            </Button>
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => navigate('/admin/communications/email-campaigns/new')}
            >
              Create Campaign
            </Button>
          </Stack>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}
        <Alert severity="info">
          Tip: duplicate your best-performing campaign, adjust audience and schedule, then reuse it as a template.
        </Alert>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Campaign snapshot (current page)</Typography>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={4} md={2}>
                <Chip label={`Draft: ${summary.draft}`} size="small" />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Chip label={`Scheduled: ${summary.scheduled}`} size="small" color="info" />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Chip label={`Sending: ${summary.sending}`} size="small" color="warning" />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Chip label={`Sent: ${summary.sent}`} size="small" color="success" />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Chip label={`Cancelled: ${summary.cancelled}`} size="small" />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Chip label={`Failed: ${summary.failed}`} size="small" color="error" />
              </Grid>
            </Grid>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              size="small"
              select
              label="Status"
              value={filters.status}
              onChange={(event) => {
                setFilters((prev) => ({ ...prev, status: event.target.value }));
                setPage(1);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_FILTERS.filter(Boolean).map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Search"
              value={filters.q}
              onChange={(event) => {
                setFilters((prev) => ({ ...prev, q: event.target.value }));
                setPage(1);
              }}
              fullWidth
            />

            <TextField
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={filters.date_from}
              onChange={(event) => {
                setFilters((prev) => ({ ...prev, date_from: event.target.value }));
                setPage(1);
              }}
            />

            <TextField
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={filters.date_to}
              onChange={(event) => {
                setFilters((prev) => ({ ...prev, date_to: event.target.value }));
                setPage(1);
              }}
            />

            <Button variant="text" onClick={resetFilters}>
              Clear filters
            </Button>
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary">
            Filters apply immediately. Results are paginated below.
          </Typography>
        </Paper>

        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Scheduled</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {empty ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No campaigns found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}

                {rows.map((campaign) => (
                  <TableRow key={campaign.id} hover>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {campaign.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {campaign.subject || '-'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <EmailCampaignStatusChip status={campaign.status} />
                    </TableCell>
                    <TableCell>{campaign.audience_summary || campaign.audience_mode}</TableCell>
                    <TableCell>
                      {campaign.send_at
                        ? new Date(campaign.send_at).toLocaleString()
                        : campaign.next_run_at
                        ? new Date(campaign.next_run_at).toLocaleString()
                        : '-'}
                    </TableCell>
                    <TableCell>{campaign.created_by_user?.name || campaign.created_by || '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View details">
                        <IconButton onClick={() => navigate(`/admin/communications/email-campaigns/${campaign.id}`)}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Duplicate">
                        <IconButton onClick={() => handleDuplicate(campaign)}>
                          <ContentCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Cancel scheduled">
                        <span>
                          <IconButton
                            onClick={() => handleCancel(campaign)}
                            disabled={!canCancel(campaign)}
                            color="error"
                          >
                            <CancelOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title={campaign.can_delete ? 'Delete' : 'Only draft, cancelled or failed campaigns can be deleted'}>
                        <span>
                          <IconButton
                            onClick={() => handleDelete(campaign)}
                            disabled={!campaign.can_delete}
                            color="error"
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={Number(meta?.total || 0)}
            page={pageIndex}
            onPageChange={(_, nextPage) => setPage(nextPage + 1)}
            rowsPerPage={Number(perPage || 15)}
            onRowsPerPageChange={(event) => {
              setPerPage(Number(event.target.value || 15));
              setPage(1);
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
          />
        </Paper>
      </Stack>

      <EmailCampaignTourDialog
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        onDismiss={finishTour}
        onFinish={finishTour}
        title="Email Campaigns Overview"
        steps={LIST_TOUR_STEPS}
      />
    </Box>
  );
}
